import {
  SITE_CHANNEL,
  SITE_SCOPE,
  useDataInvalidation,
} from '@tecnobit-srl/echo';
import { usePage, useReload } from '@tecnobit-srl/router';
import { type ReactNode } from 'react';

/**
 * Keeps the retained shared props correct for the whole session. Each retained
 * prop gets its own subscription to the invalidation slices the classification
 * declares for it (shared as `props.sharedProps.retained`), delivered on the
 * public site channel: a matching invalidation refetches just that prop with a
 * silent partial reload, merged into the page by the router.
 *
 * The visibility fallback is opted out deliberately: a retained prop's
 * correctness contract is "refreshed by invalidation, never on its own" —
 * re-fetching on every tab-visible would be an expiry mechanism in disguise
 * (and would re-download the very bytes retention exists to save).
 */
function RetainedSharedProp({
  prop,
  slices,
}: {
  prop: string;
  slices: string[];
}): ReactNode {
  const reload = useReload();

  useDataInvalidation({
    slices,
    scope: SITE_SCOPE,
    channel: { name: SITE_CHANNEL, visibility: 'public' },
    visibilityFallback: false,
    // The outcome is of no use here: a silent reload that failed already threw, and the
    // invalidation contract wants `void | Promise<void>`.
    refresh: async () => {
      await reload([prop], true);
    },
  });

  return null;
}

export function RetainedSharedProps(): ReactNode {
  const page = usePage();
  const retained = page.sharedProps?.retained ?? {};

  return (
    <>
      {Object.entries(retained).map(([prop, slices]) => (
        <RetainedSharedProp key={prop} prop={prop} slices={slices} />
      ))}
    </>
  );
}
